import { I18n } from "@assets/strings";
import { Range } from "immutable";
import React from "react";
import { Link } from "react-router";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { LightCourse } from "@domain/lms/course/course";
import { Feedback } from "@domain/lms/course/feedback/feedback";
import { fullName } from "@domain/user/utils";
import { Card } from "@ui/components/containers/card";
import { KebabMenu } from "@ui/components/menus/kebabMenu";
import { SimpleMenuContent } from "@ui/components/menus/simpleMenuContent";
import { ProfilePicture } from "@ui/components/profile/profilePicture";
import { useModalTools } from "@ui/modals/modalContext";
import { gap } from "@ui/styles/utils";
import { FeedbackModal } from "./feedbackModal";
import { Star } from "./star";

interface FeedbackViewProps extends React.HTMLAttributes<HTMLDivElement> {
	feedback: Feedback;
	course: LightCourse;
}
export const FeedbackView: React.FC<FeedbackViewProps> = ({ feedback, course, ...htmlProps }) => {
	const { user, score, text, lastUpdateDate } = feedback;
	const currentProfile = useCurrentProfile();
	const { courseService } = useServices();
	const { createModal } = useModalTools();
	const isAuthor = currentProfile?.id === user.id;

	return (
		<Container {...htmlProps}>
			<Link to={Path.Profile(user)}>
				<ProfilePicture picture={user.avatar} size={40} />
			</Link>
			<FeedbackContent>
				<Link to={Path.Profile(user)}>
					<Author>{fullName(user)}</Author>
				</Link>
				{text && <Comment>{text}</Comment>}
				<ScoreAndDate>
					<Score>
						{Range(1, 6).map((n) => (
							<Star key={n} $isFilled={score >= n} />
						))}
						<div>{I18n.t(`course.feedback.stars.${score}`)}</div>
					</Score>
					<div>{I18n.t("course.feedback.date", { date: lastUpdateDate.toLocaleDateString() })}</div>
				</ScoreAndDate>
			</FeedbackContent>
			{currentProfile && (
				<FeedbackMenu>
					<SimpleMenuContent
						onEdit={
							isAuthor ? () => createModal((close) => <FeedbackModal close={close} course={course} />) : undefined
						}
						onDelete={isAuthor ? () => courseService.deleteFeedback(course.id) : undefined}
						reportInfos={
							isAuthor
								? undefined
								: {
										contentId: course.id,
										contentType: "COURSE",
									} // TODO REPLACE WITH FEEDBACK ID
						}
					/>
				</FeedbackMenu>
			)}
		</Container>
	);
};

const Container = styled(Card)`
	padding: 16px;
	display: flex;
	position: relative;

	> :first-child {
		margin-right: 16px;
	}
`;

const FeedbackContent = styled.div`
	${({ theme }) => theme.textStyles.primary};
	flex: 1;
`;

const Author = styled.b``;

const Comment = styled.div``;

const ScoreAndDate = styled.div`
	margin-top: 5px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	${({ theme }) => theme.textStyles.label};
	color: ${({ theme }) => theme.colors.textTertiary};

	@media (max-width: 900px) {
		flex-direction: column;
		align-items: flex-end;
	}
`;

const FeedbackMenu = styled(KebabMenu)`
	position: absolute;
	right: 10px;
	top: 10px;
`;

const Score = styled.div`
	display: flex;
	align-items: center;
	${gap(6, "row")}
	svg {
		width: 18px;
	}
	@media (max-width: 900px) {
		align-self: flex-start;
	}
`;
